import React, {
  useEffect,
  useState,
} from "react";

import {
  InvoiceCommercialResponse,
  InvoiceCommercialUpdate,
} from "@/api";

import InvoiceCommercialsView from "./InvoiceCommercialsView";
import InvoiceCommercialsEdit from "./InvoiceCommercialsEdit";

import {
  AdminOrderUpdateService,
} from "../../service/adminOrderUpdateServices";

export interface InvoiceCommercialsSectionProps {
  value: InvoiceCommercialResponse;

  onSaved?: () => void;
}

export default function InvoiceCommercialsSection({
  value,
  onSaved
}: InvoiceCommercialsSectionProps) {
  const [editing, setEditing] =
    useState(false);

  const [draft, setDraft] =
    useState<InvoiceCommercialResponse>(value);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  /*
   * --------------------------------------------------
   * Synchronize external value when not editing
   * --------------------------------------------------
   */

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  /*
   * --------------------------------------------------
   * Edit
   * --------------------------------------------------
   */

  const handleEdit = () => {
    setDraft({
      ...value,
    });

    setError(null);
    setEditing(true);
  };

  /*
   * --------------------------------------------------
   * Cancel
   * --------------------------------------------------
   */

  const handleCancel = () => {
    setDraft({
      ...value,
    });

    setError(null);
    setEditing(false);
  };

  /*
   * --------------------------------------------------
   * Save
   * --------------------------------------------------
   */

  const handleSave = async () => {
  try {
    setSaving(true);
    setError(null);
    console.log("INVOICE COMMMERCIAL UPDATE PAYLOAD: ", JSON.stringify(draft));
    const commercialId = draft.id;

    if (commercialId == null) {
      throw new Error(
        "Invoice commercial ID is missing.",
      );
    }

    const payload: InvoiceCommercialUpdate = {
      id: commercialId,

      currency:
        draft.currency ?? null,

      payment_mode:
        draft.payment_mode ?? null,

      commercial_discount_amount:
        draft.commercial_discount_amount ?? null,

      round_off_amount:
        draft.round_off_amount ?? null,

      valid_until:
        draft.valid_until ?? null,
    };

    console.log(
      "INVOICE COMMERCIAL UPDATE PAYLOAD:",
      JSON.stringify(
        payload,
        null,
        2,
      ),
    );

    const updated =
      await AdminOrderUpdateService.updateInvoiceCommercial(
        commercialId,
        payload,
      );

    /*
     * Backend response is authoritative.
     */
    if (onSaved) {
      await onSaved();
    }
        /*
     * Leave edit mode only after
     * successful persistence.
     */
    setEditing(false);

  } catch (err) {
    console.error(
      "Failed to update invoice commercials",
      err,
    );

    setError(
      err instanceof Error
        ? err.message
        : "Failed to update invoice commercials.",
    );
  } finally {
    setSaving(false);
  }
};

  return (
    <section className="rounded-xl border bg-white p-6">

      {/* =================================================
          EDIT MODE
      ================================================= */}

      {editing ? (
        <>
          <SectionHeader
            title="Invoice Commercials"
            description="Edit invoice commercial adjustments."
            editing
            saving={saving}
            onCancel={handleCancel}
            onSave={() => void handleSave()}
          />

          <InvoiceCommercialsEdit
            value={draft}
            onChange={setDraft}
          />

          {error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </>
      ) : (

        /* =================================================
           VIEW MODE
        ================================================= */

        <>
          <SectionHeader
            title="Invoice Commercials"
            description="Review invoice commercial adjustments."
            onEdit={handleEdit}
          />

          <InvoiceCommercialsView
            value={value}
          />
        </>
      )}
    </section>
  );
}

/*
 * ==================================================
 * HEADER
 * ==================================================
 */

interface SectionHeaderProps {
  title: string;
  description: string;

  editing?: boolean;
  saving?: boolean;

  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
}

function SectionHeader({
  title,
  description,
  editing = false,
  saving = false,
  onEdit,
  onCancel,
  onSave,
}: SectionHeaderProps) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold">
          {title}
        </h2>

        <p className="mt-1 text-sm text-gray-500">
          {description}
        </p>
      </div>

      {editing ? (
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-md bg-black px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border px-3 py-2 text-sm font-medium"
        >
          Edit
        </button>
      )}
    </div>
  );
}